import {useEffect, useMemo, useRef, useState} from "react";

import {AgGridReact} from "ag-grid-react";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import {useDispatch, useSelector} from "react-redux";
import {withTranslation} from "react-i18next";
import {isEmpty} from "lodash";

import fpRouteService from "@plannel/services/fp/fp-route-service";
import RouteGroupPopup from "@plannel/pages/factory-plan/common/popup/RouteGroupPopup";
import StagePopup from "@plannel/pages/factory-plan/common/popup/StagePopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SearchOptsGroup from "@plannel/pages/factory-plan/common/component/SearchOptsGroup";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import {AddButton, DownloadButton, RemoveButton, SaveButton} from "@plannel/components/ActionIconButton";
import Button from "@mui/material/Button";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

// 이 화면의 고정 조회조건 3개(§10 — DB 동적 표시 제어 제거, 항상 이 순서로 전부 노출).
const SEARCH_FIELD_LABELS = { routeCdNm: "FP_ROUTE", routeGrpCdNm: "FP_ROUTE_GRP", stageCdNm: "FP_STAGE" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const { snackBarMessageColor } = ColorUtil;

// old validRules required 4개(routeCd/routeGrpCd/stageCd/plantCd) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "routeCd", label: "FP_ROUTE_CD" },
  { field: "routeGrpCd", label: "FP_ROUTE_GRP_CD" },
  { field: "stageCd", label: "FP_STAGE_CD" },
  { field: "plantCd", label: "FP_PLANT_CD" }
];

const Route = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  // 초기값은 비워둠 → PlanScope 컴포넌트가 목록 로드 후 맨 위 항목을 자동 선택(onChange)하고,
  // 그 값이 planScope에 들어오면 useEffect가 조회를 트리거. (retrieveRoute는 planScope 비면 skip)
  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  // §10 SearchOptsGroup — 화면이 쓸 수 있는 검색필드 전체(고정 카탈로그)를 객체 하나로 미리 선언.
  // 표시 필드는 SEARCH_FIELD_LABELS 키 그대로 고정(더 이상 DB로 동적 제어하지 않음).
  const [searchFields, setSearchFields] = useState({
    routeCdNm: reduxViewState?.routeCdNm ?? "",
    routeGrpCdNm: reduxViewState?.routeGrpCdNm ?? "",
    stageCdNm: reduxViewState?.stageCdNm ?? "",
  });

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);
  // editable: true면 RouteGroupPopup이 열릴 때부터 편집(관리) 모드 — RouteGroupMasterPopup을
  // 따로 만들지 않고 이 팝업의 편집 토글로 통합(RouteGroupPopup.js 주석 참고).
  const [routeGrpPopup, setRouteGrpPopup] = useState({ open: false, node: null, editable: false });
  const [stagePopup, setStagePopup] = useState({ open: false, node: null });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [route, setRoute] = useState([]);

  // 작업분할 유형(FP_DIVIDE_TP_CD) 콤보 값 — §8 공통코드 콤보 공통 훅(common/grid.js).
  const divideTpValuesRef = useCommonCodeValues("FP_DIVIDE_TP_CD");

  // 조회 요청 토큰 — planScope를 빠르게 바꾸면 이전 요청 응답이 나중에 도착해(순서 뒤바뀜)
  // 최신 결과를 덮어쓰는 걸 막기 위해, 최신 요청의 응답만 반영한다.
  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title, viewName }),
    autoSizeStrategy: { type: "fitCellContents", colIds: ['divideTpCd', 'lazyJcTimeYn'] },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // planScope는 서버 조회 조건(스코프 파티션), 아래 3개 코드는 이미 로드된 데이터셋을 AG Grid
  // 외부 필터(isExternalFilterPresent/doesExternalFilterPass)로 즉시 필터(§6-1 케이스3).
  // rowData 배열 자체는 절대 바꾸지 않는다 — rowData를 매번 새 배열(filteredXxx)로 갈아치우면
  // getRowId 없는 상태에서 grid row model이 통째로 재생성되어, applyTransaction으로 추가한
  // 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다(§12 흔한 함정 참고).
  // 코드/명칭 중 하나라도 부분일치(대소문자 무시)하면 통과 — 서버 makeWhereClause와 동일 규칙.
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { routeCdNm, routeGrpCdNm, stageCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (routeCdNm && !(hit(r.routeCd, routeCdNm) || hit(r.routeNm, routeCdNm))) return false;
    if (routeGrpCdNm && !(hit(r.routeGrpCd, routeGrpCdNm) || hit(r.routeGrpNm, routeGrpCdNm))) return false;
    if (stageCdNm && !(hit(r.stageCd, stageCdNm) || hit(r.stageNm, stageCdNm))) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const onSearchFieldChange = (tpCd, value) => setSearchFields(prev => ({ ...prev, [tpCd]: value }));

  // 딥링크 수신(fp-master-screen-guide.md §13-2) — FLO 화면에서 공정 노드를 더블클릭하면 이 화면으로
  // 이동하면서 공정 코드를 채워준다. 케이스3(클라 외부 필터)이라 setState만으로 반영된다(§13-3).
  const incomingNav = useSelector((state) => state.historyState.history);
  useEffect(() => {
    if (incomingNav !== viewName) return;
    const freshState = reduxUtil.getViewState(viewName);
    if (!freshState) return;

    if (freshState.planScope) setPlanScope(freshState.planScope);
    setSearchFields((prev) => ({
      routeCdNm: freshState.routeCdNm ?? prev.routeCdNm,
      routeGrpCdNm: freshState.routeGrpCdNm ?? prev.routeGrpCdNm,
      stageCdNm: freshState.stageCdNm ?? prev.stageCdNm,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingNav]);

  const columnDefs = [
    // id는 axios-bigint의 useNativeBigInt로 파싱돼 typeof "bigint" — AG Grid 타입 추론이
    // number/string/boolean/date 어디에도 안 걸려 "object"로 폴백해 경고를 내므로 추론을 끈다.
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old setCodeColumnStyle 대응 — 신규 행(id 없음)만 코드 편집 가능, 저장된 행은 PK성 코드 잠금.
    { headerName: "FP_ROUTE_CD", field: "routeCd", colId: "routeCd", cellClass: "stringType", width: 180,
      editable: params => !params.data?.id },
    { headerName: "FP_ROUTE_NM", field: "routeNm", colId: "routeNm", cellClass: "stringType", width: 240 },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", cellClass: "stringType", width: 110 },
    { headerName: "FP_DIVIDE_TP_CD", field: "divideTpCd", colId: "divideTpCd", cellClass: "stringType", width: 110,
      type: ["centerAligned"], cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      // 셀 표시는 value 그대로, 드롭다운 목록만 "value - label"로 (§8 공통코드 콤보 공통 헬퍼).
      cellEditorParams: () => codeComboCellEditorParams("FP_DIVIDE_TP_CD", divideTpValuesRef, t) },
    { headerName: "FP_ROUTE_GRP_CD", field: "routeGrpCd", colId: "routeGrpCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setRouteGrpPopup({ open: true, node, editable: false }) } },
    { headerName: "FP_ROUTE_GRP_NM", field: "routeGrpNm", colId: "routeGrpNm", type: ["nonEditableColumn"], width: 240 },
    // 스테이지 그룹(2줄 헤더, 접기/펼치기) — 접힘: 코드만(대표), 펼침: 명칭/공장코드/공장명 추가 (레거시 FP_STAGE 그룹 대응)
    // 전 컬럼 고정 width, old width 비율(150:200:80:95:125) 유지하되 전체적으로 소폭 확장(§3-2).
    {
      headerName: "FP_STAGE", groupId: "stage", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_STAGE_CD", field: "stageCd", colId: "stageCd", cellClass: "stringType", width: 180,
          editable: false, cellRenderer: CodePickerRenderer,
          cellRendererParams: { onOpenPopup: node => setStagePopup({ open: true, node }) } },
        { headerName: "FP_STAGE_NM", field: "stageNm", colId: "stageNm", type: ["nonEditableColumn"], width: 240, columnGroupShow: "open" },
        { headerName: "FP_PLANT_CD", field: "plantCd", colId: "plantCd", type: ["nonEditableColumn"], width: 180, columnGroupShow: "open" },
        { headerName: "FP_PLANT_NM", field: "plantNm", colId: "plantNm", type: ["nonEditableColumn"], width: 240, columnGroupShow: "open" }
      ]
    },
    { headerName: "FP_LAZY_JC_TM_YN", field: "lazyJcTimeYn", colId: "lazyJcTimeYn", type: ["booleanColumn", "centerAligned"], width: 120 },
    // 수정 정보(audit) 그룹(2줄 헤더, 접기/펼치기) — 접힘: 수정일시만(대표), 펼침: 수정자/생성일시/생성자 추가 (레거시 FP_COL_AUDIT 그룹 대응)
    // old textAlignment: "center" 4개 전부 대응, width는 old 비율 유지 + 소폭 확장.
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveRoute = () => {
    if (!planScope) return;

    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    // planScope 단위로 전량 로드 → 공정/공정그룹/스테이지 코드 필터는 서버가 아니라 AG Grid 외부 필터(클라)에서 처리.
    fpRouteService.getAll({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;   // 최신 요청이 아니면 무시(순서 뒤바뀜 방지)
        const results = response.data;
        if (isEmpty(results)) {
          setRoute([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setRoute(results);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        const reduxState = reduxUtil.createObjectState(viewName, { planScope, ...searchFields });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
        gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const insertRow = () => {
    const newRow = {
      id: null,
      planScope,
      routeCd: "",
      routeNm: "",
      descTxt: "",
      divideTpCd: "Y",
      routeGrpCd: "",
      stageCd: "",
      stageNm: "",
      plantCd: "",
      plantNm: "",
      lazyJcTimeYn: false,
      batchRouteYn: false
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeRoute = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpRouteService.remove(selectedRows)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          retrieveRoute();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveRoute = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setProgressOn(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      setProgressOn(false);
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpRouteService.upsert(allStateData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        setProgressOn(false);
        retrieveRoute();
      });
  };

  const onSelectRouteGroup = (row) => {
    const node = routeGrpPopup.node;
    if (node) {
      node.setDataValue("routeGrpCd", row.routeGrpCd);
      node.setDataValue("routeGrpNm", row.routeGrpNm);
    }
  };

  // plantCd/plantNm까지 4개를 write-back한다 — StagePopup이 stage 리프에 상위 plant를 얹어
  // 넘겨주고(StagePopup.js:88), plantCd는 nonEditableColumn이라 이 경로가 유일한 공급원이다.
  // 빠뜨리면 REQUIRED_FIELDS 검증에 걸려 신규 행 저장이 원천 차단된다. Resource.js:289와 동일.
  const onSelectStage = (row) => {
    const node = stagePopup.node;
    if (node) {
      node.setDataValue("stageCd", row.stageCd);
      node.setDataValue("stageNm", row.stageNm);
      node.setDataValue("plantCd", row.plantCd);
      node.setDataValue("plantNm", row.plantNm);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      setProgressOn(true);
      saveRoute();
    } else if (action === "DELETE") {
      removeRoute();
    }
  };

  // 서버 조회는 planScope(스코프 파티션) 변경 시에만 즉시. (공정/공정그룹/스테이지 코드 필터는 서버를
  // 타지 않고 AG Grid 외부 필터로 클라이언트에서 처리하므로 디바운스 불필요 — planScope는 드롭다운 이산 변경.)
  useEffect(() => {
    if (!gridLoading) return;    
    retrieveRoute();   // DataState.initialize는 retrieveRoute의 .then에서 데이터 로드 후 1회 수행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, planScope]);
  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <SearchOptsGroup
          fields={searchFields}
          fieldKeys={SEARCH_FIELD_KEYS}
          onChange={onSearchFieldChange}
          labels={SEARCH_FIELD_LABELS}
        />
        <Box sx={{ display: 'flex' }}>
          <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })} />
          <Button size="small" onClick={() => setRouteGrpPopup({ open: true, node: null, editable: true })}>
            {t("FP_ROUTE_GROUP_MST")}
          </Button>
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        </Box>
      </FilterContainer>
      <Box id="fp-route-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={route}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridReady={onGridReady}
        />
      </Box>
      <RouteGroupPopup
        open={routeGrpPopup.open}
        onClose={() => setRouteGrpPopup({ open: false, node: null, editable: false })}
        planScope={planScope}
        onSelect={onSelectRouteGroup}
        editable={routeGrpPopup.editable}
      />
      <StagePopup
        open={stagePopup.open}
        onClose={() => setStagePopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectStage}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(Route);
